
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/store/cart';
import { useToast } from '@/components/ui/use-toast';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

interface StoreProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  discount_price?: number;
  images: string[];
  design_image_url?: string;
  store_name: string;
  category: string;
  available_colors: string[];
  available_sizes: string[];
  print_options: string[];
  mockup_images: Record<string, Record<string, string>>;
}

interface StoreProductModalProps {
  product: StoreProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export const StoreProductModal: React.FC<StoreProductModalProps> = ({
  product,
  isOpen,
  onClose,
}) => {
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedPrintOption, setSelectedPrintOption] = useState<string>('');
  const { addItem } = useCartStore();
  const { toast } = useToast();

  React.useEffect(() => {
    if (product && isOpen) {
      setSelectedColor(product.available_colors[0] || '');
      setSelectedSize(product.available_sizes[0] || '');
      setSelectedPrintOption(product.print_options[0] || '');
    }
  }, [product, isOpen]);

  if (!product) return null;

  const getProductImages = () => {
    const images = [];
    
    // Imagem principal com design
    if (product.images[0]) {
      images.push({
        url: product.images[0],
        hasDesign: true
      });
    }
    
    // Imagens do mockup baseadas na categoria e cor selecionada
    const categoryKey = product.category === 'tshirts' ? 'tshirt' : 'hoodie';
    const colorKey = selectedColor.toLowerCase();
    
    if (product.mockup_images[categoryKey]?.[colorKey]) {
      images.push({
        url: product.mockup_images[categoryKey][colorKey],
        hasDesign: true
      });
    }
    
    return images;
  };

  const handleAddToCart = () => {
    if (!selectedColor || !selectedSize || !selectedPrintOption) {
      toast({
        title: 'Selecione todas as opções',
        description: 'Por favor, selecione cor, tamanho e opção de estampa',
        variant: 'destructive',
      });
      return;
    }

    addItem({
      id: product.id,
      title: product.title,
      price: product.discount_price || product.price,
      imageUrl: product.images[0],
      quantity: 1,
      size: selectedSize,
      color: selectedColor,
      printOption: selectedPrintOption,
    });

    toast({
      title: 'Produto adicionado ao carrinho',
      description: `${product.title} foi adicionado ao seu carrinho`,
    });

    onClose();
  };

  const productImages = getProductImages();
  const finalPrice = product.discount_price || product.price;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-bold">{product.title}</DialogTitle>
          <p className="text-sm text-muted-foreground font-medium">por {product.store_name}</p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-6">
          {/* Carousel de Imagens */}
          <div className="space-y-4">
            <Carousel className="w-full">
              <CarouselContent>
                {productImages.map((image, index) => (
                  <CarouselItem key={index}>
                    <div className="aspect-square relative bg-muted rounded-lg overflow-hidden border shadow-sm">
                      <img
                        src={image.url}
                        alt={`${product.title} - Imagem ${index + 1}`}
                        className="object-cover w-full h-full"
                      />
                      
                      {/* Design overlay */}
                      {image.hasDesign && product.design_image_url && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={product.design_image_url}
                            alt="Design aplicado"
                            className="max-w-[35%] max-h-[35%] object-contain filter drop-shadow-lg"
                          />
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {productImages.length > 1 && (
                <>
                  <CarouselPrevious className="bg-background/95 border text-foreground hover:bg-muted/50 backdrop-blur-sm shadow-md" />
                  <CarouselNext className="bg-background/95 border text-foreground hover:bg-muted/50 backdrop-blur-sm shadow-md" />
                </>
              )}
            </Carousel>
          </div>

          {/* Opções do Produto */}
          <div className="space-y-6 bg-card rounded-lg p-6 border">
            <div className="border-b pb-4">
              <h3 className="text-xl font-bold mb-3 text-card-foreground">Preço</h3>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                  R$ {finalPrice.toFixed(2)}
                </span>
                {product.discount_price && (
                  <span className="text-xl text-muted-foreground line-through font-medium">
                    R$ {product.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-card-foreground">Cor</h3>
              <RadioGroup
                value={selectedColor}
                onValueChange={setSelectedColor}
                className="flex flex-wrap gap-3"
              >
                {product.available_colors.map((color) => (
                  <div key={color} className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                    <RadioGroupItem 
                      value={color} 
                      id={`color-${color}`} 
                      className="border-2 border-primary text-primary-foreground data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                    />
                    <Label 
                      htmlFor={`color-${color}`} 
                      className="text-foreground font-medium cursor-pointer hover:text-primary transition-colors select-none"
                    >
                      {color}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-card-foreground">Tamanho</h3>
              <RadioGroup
                value={selectedSize}
                onValueChange={setSelectedSize}
                className="flex flex-wrap gap-3"
              >
                {product.available_sizes.map((size) => (
                  <div key={size} className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                    <RadioGroupItem 
                      value={size} 
                      id={`size-${size}`} 
                      className="border-2 border-primary text-primary-foreground data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                    />
                    <Label 
                      htmlFor={`size-${size}`} 
                      className="text-foreground font-medium cursor-pointer hover:text-primary transition-colors select-none"
                    >
                      {size}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-card-foreground">Opção de Estampa</h3>
              <RadioGroup
                value={selectedPrintOption}
                onValueChange={setSelectedPrintOption}
                className="flex flex-wrap gap-3"
              >
                {product.print_options.map((option) => (
                  <div key={option} className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                    <RadioGroupItem 
                      value={option} 
                      id={`print-${option}`} 
                      className="border-2 border-primary text-primary-foreground data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                    />
                    <Label 
                      htmlFor={`print-${option}`} 
                      className="text-foreground font-medium cursor-pointer hover:text-primary transition-colors select-none"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="pt-6 border-t">
              <Button 
                onClick={handleAddToCart} 
                className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-primary hover:border-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="mr-3 h-5 w-5" />
                Adicionar ao Carrinho (R$ {finalPrice.toFixed(2)})
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
